import { useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useMedia, useBreakpointValue } from '@gluestack-ui/themed';

import { useSelectedTask } from '../../../common/hooks/student/useSelectedTask';
import { useAppStateStore } from '../../../common/context/AppStateStore';
import { useTaskSubmissionActions } from '../stores/TaskSubmissionStore';
import { useTaskSafetyFuse } from './useTaskSafetyFuse';
import { useDialog } from '../../../common/context/DialogContext';
import { isUUID } from '../../../common/types/ids';
import log from '../../../common/utils/logger';
import TaskScreenWebLayout from '../screenLayouts/TaskScreenWebLayout';
import TaskScreenMobileLayout from '../screenLayouts/TaskScreenMobileLayout';

/**
 * Custom hook for TaskScreen logic.
 * Handles task data, safety fuse (refresh conflict), layout selection,
 * and submission store initialization.
 */
export const useTaskScreen = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const {
    task, resources, thumbnails, isTasksLoading,
  } = useSelectedTask();
  const { selectedModule } = useAppStateStore();
  const { t } = useTranslation();
  const media = useMedia();
  const responsiveLayout = useBreakpointValue({
    base: TaskScreenMobileLayout,
    lg: TaskScreenWebLayout,
  });
  const { showDialog } = useDialog();

  const { conflict, resolveConflict } = useTaskSafetyFuse(task?.id);
  const { initializeFromTask, reset } = useTaskSubmissionActions();

  const taskIsGatedAndDone = useMemo(
    () => !!task
    && task?.evaluation?.completionPolicyEffective === 'gated'
    && task?.submission?.state === 'marked_as_done',
    [task],
  );

  // Initialize/Cleanup submission store
  // IMPORTANT: Only trigger on task ID changes, NOT on task object reference changes.
  // This prevents race conditions during autosave where:
  // 1. Save completes -> React Query cache updates -> new task object reference
  // 2. useEffect fires -> initializeFromTask overwrites fresh store data with stale cache
  // ETag and content management during saves is handled by the controller's updateStoreEtag.
  useEffect(() => {
    if (!task) {
      return () => {};
    }
    initializeFromTask(task);
    return () => {};
  }, [task?.id, initializeFromTask]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset store ONLY on unmount
  useEffect(() => () => {
    reset();
  }, [reset]);

  // Handle Safety Fuse Conflict
  useEffect(() => {
    if (conflict && task) {
      // Safety fuse triggered - this is expected for multi-tab scenarios
      log.warn('[useTaskScreen] Safety Fuse Conflict: User had task open in multiple tabs', {
        conflictTaskId: task.id,
        moduleId: selectedModule?.moduleId,
        context: 'safety_fuse_redirect',
      });

      showDialog({
        title: t('student.task.fuse.title', 'Task Selection Conflict'),
        message: t(
          'student.task.fuse.message',
          'You were previously viewing a different task in this tab. To prevent data issues, please re-select the task you want to work on.',
        ),
        buttons: [
          {
            text: t('common.buttons.ok', 'OK'),
            onPress: () => {
              resolveConflict('stay_current');
              reset();
              // Navigate to CourseScreen instead of ModuleScreen to avoid undefined params
              navigation.navigate('CourseScreen');
            },
          },
        ],
      });
    }
  }, [conflict, task, showDialog, t, resolveConflict, reset, navigation, selectedModule]);

  // Desktop meeting-only guard: generic event work lives on ModuleScreen.
  // Oral exams stay on TaskScreen so students can study the full question pool.
  useEffect(() => {
    if (!task) return;
    const meetingOnlyEvent = task.type === 'event';
    const isDesktop = Platform.OS === 'web' && media.md;

    if (isDesktop && meetingOnlyEvent) {
      // Validate IDs before navigating with params
      if (
        selectedModule?.moduleId
        && selectedModule?.courseId
        && isUUID(selectedModule.moduleId)
        && isUUID(selectedModule.courseId)
      ) {
        navigation.navigate('ModuleScreen', {
          moduleId: selectedModule.moduleId,
          courseId: selectedModule.courseId,
        });
      } else {
        // Fallback to CourseScreen if IDs are invalid or missing
        // Only log as error if selectedModule exists but has invalid IDs (actual bug)
        // If selectedModule is null, this is normal (navigation cleared it)
        if (selectedModule !== null) {
          log.error('[useTaskScreen] Desktop Event Task Guard: Invalid IDs in selectedModule', {
            moduleId: selectedModule.moduleId,
            courseId: selectedModule.courseId,
            taskType: task.type,
            context: 'desktop_event_task_redirect',
          });
        }
        navigation.navigate('CourseScreen');
      }
    }
  }, [task?.type, media.md, navigation, task, selectedModule]);

  // Redirect if no task
  useEffect(() => {
    if (!isTasksLoading && !task) {
      // Validate IDs before navigating with params
      if (
        selectedModule?.moduleId
        && selectedModule?.courseId
        && isUUID(selectedModule.moduleId)
        && isUUID(selectedModule.courseId)
      ) {
        navigation.navigate('ModuleScreen', {
          moduleId: selectedModule.moduleId,
          courseId: selectedModule.courseId,
        });
      } else {
        // Fallback to CourseScreen if IDs are invalid or missing
        // Only log as error if selectedModule exists but has invalid IDs (actual bug)
        // If selectedModule is null, this is normal (e.g., after module submission navigation)
        if (selectedModule !== null) {
          log.error('[useTaskScreen] Redirect to ModuleScreen failed: Invalid IDs in selectedModule', {
            moduleId: selectedModule.moduleId,
            courseId: selectedModule.courseId,
            isTasksLoading,
            hasTask: !!task,
            context: 'no_task_redirect',
          });
        }
        navigation.navigate('CourseScreen');
      }
    }
  }, [isTasksLoading, task, navigation, selectedModule]);

  const TaskScreenLayout = Platform.OS !== 'web'
    ? TaskScreenMobileLayout
    : responsiveLayout;

  return {
    task,
    resources,
    thumbnails,
    isTasksLoading,
    selectedModule,
    taskIsGatedAndDone,
    conflict,
    TaskScreenLayout,
  };
};
